import { bindable, View, ViewSlot, inject, bindingMode, Parent, Container } from 'aurelia-framework';
import { fireEvent } from 'app/shared/lib/fire-events';
import { truthy, falsy, equals, min, max, before, after } from 'app/shared/lib/noop';
import { moveToSlot, asContentProcessor, findClosestAttachedElement } from 'app/shared/lib/dom';
import { guid } from 'app/shared/lib/guid';

export interface IValidatable {
    valid: boolean | null;
    message: string | null;
    id: string;
}

export type EssRuleElement = HTMLElement & IValidatable;
export type ValidateEvent = CustomEvent<IValidatable>;

@inject(Element, Container)
export class EssRuleCustomElement implements IValidatable {

    @bindable() valid: boolean | null = null;
    @bindable({ defaultBindingMode: bindingMode.fromView }) message!: string;

    private observer: MutationObserver;
    public id: guid = guid();

    constructor(public readonly element: EssRuleElement, private container: Container) {
        this.observer = new MutationObserver(() => this.message = element.innerText);
    }

    // INFO [Matt] By noop-ing bind, we prevent Aurelia from calling validChanged on
    // initial bind. This allows us to call validChanged in attached, so that the
    // validate event can be fired once the element is attached to the DOM.
    bind() { }

    attached() {
        this.observer.observe(this.element, { characterData: true });
        this.validChanged(this.valid);
    }

    detached() {
        this.observer.disconnect();
        const el = findClosestAttachedElement(this.container);
        const detail = { valid: null, message: null, id: this.id };
        fireEvent(this.element, 'validate', detail);
        if(el) {
            fireEvent(el, 'validate', detail);
        }
    }

    validChanged(valid: boolean | null) {
        const message = valid === false ? this.message : null;
        Object.assign(this.element, { valid });
        fireEvent(this.element, 'validate', { valid, message, id: this.id });
    }

    messageChanged(message: string) {
        Object.assign(this.element, { message });
    }
}

/**
 * Validates all <ess-rule>s in a form or other element.
 * @param {Element} form - The form to validate.
 * @param {boolean} excludeModal - Indicates to exclude ess-rules defined in modals within the form.
 * @returns {boolean} - True if all <ess-rules> are valid.
 */
export function validate(form: Element, excludeModal: boolean = false): boolean {
    if(!form) { return false; }
    let rules = Array.from<EssRuleElement>(form.querySelectorAll('ess-rule'));
    if(excludeModal) {
        const modalRules = Array.from<EssRuleElement>(form.querySelectorAll('ess-modal ess-rule'));
        rules = rules.filter(r => !modalRules.includes(r));
    }

    return rules.every((rule) => !!rule.valid);
}

/**
 * Retrieves all outstanding errors from <ess-rule>s in a form or other element.
 * @param {Element} form - The form to validate.
 * @returns {string[]} - A list of all outstanding errors.
 */
export function audit(form: Element): string[] {
    return Array.from<EssRuleElement>(form.querySelectorAll('ess-rule'))
        .filter((rule) => !rule.valid)
        .map((rule) => rule.message!);
}

/**
 * @description Will move all <ess-rule> to <slot name="helper"> when passed to @processContent.
 */
export const moveRulesToHelperSlot = asContentProcessor((el: Element) => moveToSlot(el, 'ess-rule', 'helper'));

/**
 * @description Collection of common validation rules as functions.
 */
export const Rules = {
    equals,
    truthy,
    required: truthy,
    falsy,
    min,
    max,
    before,
    after,
};

// INFO [Matt] Putting this here makes it so that importing the <ess-rule> component
// automatically makes $rules available on every containing view.
export class RulesViewEngineHooks {
    beforeBind(view: View) {
        Object.assign(view.overrideContext, { $rules: Rules });
    }
}
